<script setup>
import { ref, computed, onMounted } from 'vue'
import { getI18n, getLocalizedItems } from '@/plugins/i18n'
import { useAccountingStore } from '@/stores/accounting.store'
import { usePlanningStore } from '@/stores/planning.store'
import { useWebSiteStore } from '@/stores/web-site.store'
import { useNotify } from '@/composables/notify'
import { useDetectDevice } from '@/composables/device'
import { usePdfPreview } from '@/composables/pdf-preview'
import { useStepper } from '@/composables/stepper'
import { usePrintSettings, useReportColumnTemplates } from '@/composables/report-column-template'

import PdfPreviewPanel from '@/components/shared/PdfPreviewPanel.vue'
import PrintDialogLayout from '@/components/shared/PrintDialogLayout.vue'
import StepperNavigation from '@/components/shared/StepperNavigation.vue'
import PrintContentEditor from '@/components/accounting/PrintContentEditor.vue'

const props = defineProps({
  modelValue: {
    type: Object,
    required: true,
  },
})

const emit = defineEmits(['cancel'])

const i18n = getI18n()
const accountingStore = useAccountingStore()
const planningStore = usePlanningStore()
const webSiteStore = useWebSiteStore()
const { notifySuccess } = useNotify()
const { isMobile } = useDetectDevice()

const selectedRecipient = ref(null)
const selectedEmissionDate = ref(null)
const selectedTransportReason = ref(null)
const selectedCollectedBy = ref(null)
const config = ref(null)
const templateId = ref(null)
const { documentOptions } = useReportColumnTemplates()
const settings = usePrintSettings({ config, templateId, printTemplateId: ref(null) })
const { step, nextStep, prevStep } = useStepper()


const transportReasonEnum = Object.freeze({
  SALE: 0,
  SUBCONTRACT: 1,
  RETURN: 2,
  APPROVAL: 3,
  INTERNAL_TRANSFER: 4,
})

const transportReasonLabels = [
  i18n.t('common.transportReason.sale'),
  i18n.t('common.transportReason.subcontract'),
  i18n.t('common.transportReason.return'),
  i18n.t('common.transportReason.approval'),
  i18n.t('common.transportReason.internalTransfer'),
]

const transportReasonOptions = computed(() =>
  getLocalizedItems(Object.keys(transportReasonEnum).map((k, idx) => ({ id: transportReasonEnum[k], name: transportReasonLabels[idx] })))
)

const companyOptions = computed(() =>
  planningStore.companies.filter(c => !c.archived).map(c => ({ id: c.id, name: c.name, address: c.address, vat_number: c.vat_number }))
)

const onFreeTextBlur = (modelRef, evt) => {
  const val = evt?.target?.value?.trim()
  if (!val) return
  const current = modelRef.value
  const currentName = typeof current === 'string' ? current : current?.name
  if (val !== currentName) {
    modelRef.value = val
    loadPreview()
  }
}

const freeTextName = (value) => (typeof value === 'string' ? value : value.name)

const getPrintParams = () => ({
  ...(selectedRecipient.value
    ? (typeof selectedRecipient.value === 'object' && selectedRecipient.value.id
        ? { company_id: selectedRecipient.value.id }
        : { recipient_name: freeTextName(selectedRecipient.value) })
    : {}),
  ...(selectedEmissionDate.value ? { emission_date: selectedEmissionDate.value } : {}),
  ...(selectedTransportReason.value !== null ? { transport_reason: selectedTransportReason.value } : {}),
  ...(selectedCollectedBy.value ? { collected_by: selectedCollectedBy.value } : {}),
  output_config: JSON.stringify(config.value),
})

const { blobUrl: previewBlobUrl, source: previewPdfSource, isLoading: isPreviewLoading, refreshAsync: loadPreview, isPrinting, printAsync } = usePdfPreview(
  () => planningStore.previewProjectDeliveryNoteAsync(props.modelValue.id, getPrintParams())
)

const onConfigChanged = (value) => {
  config.value = value
  loadPreview()
}
const contentOptions = computed(() => documentOptions(config.value))

onMounted(async () => {
  await Promise.all([planningStore.fetchCompaniesAsync({}, true), accountingStore.fetchReportColumnsAsync(), accountingStore.fetchReportColumnTemplatesAsync()])
  settings.restore(accountingStore.printSettingTypeEnum.DELIVERY_NOTE)
  loadPreview()
})


const onPrintAsync = async() => {
  const title = `${props.modelValue.name} - ${i18n.t('common.deliveryNote')}`
  if (!await printAsync(`${title}.pdf`, title)) return
  notifySuccess(i18n.t('common.documentPrinted'))
  emit('cancel')
}
</script>
<template>
  <print-dialog-layout
    :title="i18n.t('common.print') + ' - ' + i18n.t('common.deliveryNote')"
    :source="previewPdfSource"
    :loading="isPreviewLoading"
    :printable="!!previewBlobUrl"
    :printing="isPrinting"
    @cancel="emit('cancel')"
    @print="onPrintAsync"
  >
    <template #options>
      <div v-if="config" class="q-pa-md">
        <q-stepper
          v-model="step"
          vertical
          animated
          header-nav
          class="q-py-none shadow-0"
          style="background: transparent;"
        >
          <q-step
            :name="1"
            :title="i18n.t('common.report.steps.content')"
            :caption="settings.caption.value"
            icon="mdi-file-document-outline"
            :done="step > 1"
          >
            <print-content-editor
              :model-value="config"
              :settings="settings"
              :options="contentOptions"
              :type-name="i18n.t('common.deliveryNote')"
              @update:model-value="onConfigChanged"
              @update:option="(key, value) => onConfigChanged({ ...config, [key]: value })"
              @applied="loadPreview"
            />
            <stepper-navigation class="q-mt-md" :continue="nextStep" />
          </q-step>
          <q-step
            :name="2"
            :title="i18n.t('common.documentData')"
            icon="mdi-truck-delivery-outline"
          >
            <div class="column q-gutter-md">
              <q-select
                v-model="selectedRecipient"
                :options="companyOptions"
                :label="i18n.t('print.recipient')"
                option-label="name"
                use-input
                :input-debounce="webSiteStore.getInputDebounce()"
                clearable
                dense
                outlined
                new-value-mode="add-unique"
                @blur="onFreeTextBlur(selectedRecipient, $event)"
                @update:model-value="loadPreview"
              />
              <q-input
                v-model="selectedEmissionDate"
                :label="i18n.t('common.emissionDate')"
                type="date"
                clearable
                dense
                outlined
                @update:model-value="loadPreview"
              />
              <q-select
                v-model="selectedTransportReason"
                :options="transportReasonOptions"
                :label="i18n.t('common.transportReason')"
                option-label="name"
                option-value="id"
                clearable
                dense
                outlined
                @update:model-value="loadPreview"
              />
              <q-input
                v-model="selectedCollectedBy"
                :label="i18n.t('common.collectedBy')"
                clearable
                dense
                outlined
                @update:model-value="loadPreview"
              />
            </div>
            <stepper-navigation class="q-mt-md" :back="prevStep" />
          </q-step>
        </q-stepper>
        <div v-if="isMobile" class="q-mt-md" style="height: 60vh;">
          <pdf-preview-panel :source="previewPdfSource" :loading="isPreviewLoading" />
        </div>
      </div>
    </template>
  </print-dialog-layout>
</template>
